/**
 * subscriptionService.js
 * Gestion de l'abonnement offline (valide 90 jours sans connexion)
 *
 * Le serveur émet un token signé à chaque connexion réussie.
 * Le client vérifie localement sans appel réseau.
 * Token format: base64(header).base64(payload).signature
 */

import { metaGet, metaSet } from '../db/localDB';

const SUBSCRIPTION_KEY = 'subscriptionToken';
const MAX_OFFLINE_DAYS = 10;

// ─── Stocker le token reçu du serveur ─────────────────────────────────────
export async function saveSubscriptionToken(token) {
  await metaSet(SUBSCRIPTION_KEY, token);
  await metaSet('lastOnlineCheck', new Date().toISOString());
}

// ─── Décoder le payload du token (sans vérif signature côté client) ───────
function decodeTokenPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload;
  } catch {
    return null;
  }
}

// ─── Vérifier l'abonnement localement ─────────────────────────────────────
export async function checkSubscriptionOffline() {
  const token = await metaGet(SUBSCRIPTION_KEY);
  const lastCheck = await metaGet('lastOnlineCheck');

  // Pas de token → doit se connecter
  if (!token) {
    return { valid: false, reason: 'no_token', daysLeft: 0 };
  }

  const payload = decodeTokenPayload(token);
  if (!payload) {
    return { valid: false, reason: 'invalid_token', daysLeft: 0 };
  }

  const now = new Date();

  // Vérifier expiry du token d'abonnement (subscriptionExpiry du compte)
  if (payload.subscriptionExpiry) {
    const expiry = new Date(payload.subscriptionExpiry);
    if (now > expiry) {
      return { valid: false, reason: 'subscription_expired', daysLeft: 0, expiry };
    }
  }

  // Vérifier l'expiry offline (90j depuis dernière connexion serveur)
  if (lastCheck) {
    const lastOnline = new Date(lastCheck);
    const diffDays = Math.floor((now - lastOnline) / (1000 * 60 * 60 * 24));
    const daysLeft = MAX_OFFLINE_DAYS - diffDays;

    if (diffDays >= MAX_OFFLINE_DAYS) {
      return { valid: false, reason: 'offline_expired', daysLeft: 0, diffDays };
    }

    return {
      valid: true,
      daysLeft,
      diffDays,
      lastOnline: lastCheck,
      user: payload,
      warningThreshold: daysLeft <= 3, // Avertir à 3 jours restants
    };
  }

  // Token présent mais pas de lastCheck → laisser passer
  return { valid: true, daysLeft: MAX_OFFLINE_DAYS, user: payload };
}

// ─── Vérifier via le serveur (connexion requise) ───────────────────────────
export async function checkSubscriptionOnline(apiInstance) {
  try {
    const result = await apiInstance.get('/auth/subscription-check');
    if (result.subscriptionToken) {
      await saveSubscriptionToken(result.subscriptionToken);
    }
    await metaSet('lastOnlineCheck', new Date().toISOString());
    return { valid: true, online: true, data: result };
  } catch (err) {
    // Si offline → fallback sur la vérif locale
    const local = await checkSubscriptionOffline();
    return { ...local, online: false };
  }
}

// ─── Obtenir le nombre de jours restants formaté ──────────────────────────
export async function getOfflineStatus() {
  const status = await checkSubscriptionOffline();
  return {
    ...status,
    label: status.valid
      ? status.daysLeft > 7
        ? null  // Pas d'affichage si > 7j
        : `⚠️ Reconnexion requise dans ${status.daysLeft} jour${status.daysLeft > 1 ? 's' : ''}`
      : getExpiredMessage(status.reason),
  };
}

function getExpiredMessage(reason) {
  switch (reason) {
    case 'no_token':           return '🔒 Connexion requise pour activer le mode offline';
    case 'invalid_token':      return '🔒 Session invalide — reconnectez-vous';
    case 'subscription_expired': return '⛔ Votre abonnement a expiré — contactez EasyClick Solution';
    case 'offline_expired':    return '🔒 10 jours sans connexion — reconnectez-vous au serveur';
    default:                   return '🔒 Reconnexion requise';
  }
}